import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Typography,
} from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import { columns, DataType } from "./columns";
import {
  deletePriceBetweenRegions,
  getPaginatedPrices,
  updatePriceBetweenRegions,
} from "../../../services/priceApi";
import CreateItemsServices from "../form/CreateItemsServices";
import ContainerLayout from "../../common/containerLayout";
import { PlusCircleTwoTone } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { serviceTypeListAsync } from "../../../features/serviceType/serviceTypeListSlice";

import "../../../shared/styles/list.css";
import { getAllAdmins } from "../../../services/adminApi";
import { getCurrencies } from "../../../services/currencyApi";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
}

const { Text } = Typography;
const { Search } = Input;

const PriceList = () => {
  const title = "Precios entre regiones";
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState("");
  const [ignoreFetch, setIgnoreFetch] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>(
    undefined
  );
  const [currencies, setCurrencies] = useState<{ value: number; label: string }[]>([]);

  const currencyMap = useMemo(() => {
    const map: { [key: number]: string } = {};
    currencies.forEach((c) => {
      map[c.value] = c.label;
    });
    return map;
  }, [currencies]);

  const companyMap = useMemo(() => {
    const map = new Map<number, string>();
    // @ts-ignore
    admins.forEach((a: any) => map.set(a.value, a.label));
    return map;
  }, [admins]);

  const [servicePrices, setServicePrices] = useState<
    {
      id: number;
      name: string;
      price: number;
      stopsPrice: number;
      timesPrice: number;
      driverPrice: number;
    }[]
  >([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const serviceTypes = useAppSelector((x) => x.serviceTypeList.value);
  const getAdminsData = async () => {
    const admins = await getAllAdmins();
    const mappedAdmins = admins.map((a: any) => ({
      value: a.id,
      label: a.name,
    }));
    setAdmins(mappedAdmins);
  };

  const getServices = async () => {
    await dispatch(serviceTypeListAsync());
  };

  const fetchData = async () => {
    setLoading(true);

    if (selectedCompany) {
      const { data } = await getPaginatedPrices({
        page: 1,
        take: 10000,
        name: search,
      });

      const filtered = data.filter((item: any) => {
        const hasCompany = item.company === selectedCompany;
        const hasCompanyInRegion = item.companiesPricesBetweenRegions?.some(
          (c: any) => c.companyId === selectedCompany
        );
        return hasCompany || hasCompanyInRegion;
      });

      const current = tableParams.pagination?.current || 1;
      const pageSize = tableParams.pagination?.pageSize || 10;
      const sliced = filtered.slice(
        (current - 1) * pageSize,
        current * pageSize
      );

      setData(sliced);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: filtered.length,
        },
      });
    } else {
      const { data, total } = await getPaginatedPrices({
        page: tableParams.pagination?.current || 1,
        take: tableParams.pagination?.pageSize || 10,
        name: search,
      });

      setData(data);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total,
        },
      });
    }

    setLoading(false);
    setIgnoreFetch(true);
  };

  useEffect(() => {
    getServices();
    getAdminsData();
    getCurrencies().then((data) => {
      const mapped = (data || []).map((c: any) => ({
        value: c.id,
        label: `${c.code} - ${c.name}`,
      }));
      setCurrencies(mapped);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ignoreFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams), search, selectedCompany]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter: SorterResult<DataType> | SorterResult<DataType>[],
  ) => {
    setTableParams({
      pagination,
      ...sorter,
    });
    setIgnoreFetch(false);
  };

  const onFinish = async (values: any) => {
    const {
      priceId,
      price,
      driverPrice,
      priority,
      companyIds,
      additionalStopPrice,
      additionalStopDriverPrice,
      waitingMinutePrice,
      waitingMinuteDriverPrice,
      waitingGraceMinutes,
      currencyId,
    } = values;
    setLoading(true);
    const updatePriceBetweenRegionsData = {
      id: priceId,
      price: price,
      driverPrice: driverPrice,
      priority: priority,
      companyIds,
      pricesByServices: servicePrices,
      additionalStopPrice: additionalStopPrice,
      additionalStopDriverPrice: additionalStopDriverPrice,
      waitingMinutePrice: waitingMinutePrice,
      waitingMinuteDriverPrice: waitingMinuteDriverPrice,
      waitingGraceMinutes: waitingGraceMinutes,
      currencyId: currencyId ?? null,
    };

    await updatePriceBetweenRegions(updatePriceBetweenRegionsData);
    await fetchData();
    setLoading(false);
    setOpenModal(!openModal);
  };

  const handleClick = (price: any) => {
    const companyIds: number[] =
      price.companiesPricesBetweenRegions?.map((x: any) => x.companyId) ?? [];

    if (price.company) companyIds.push(price.company);

    form.setFieldValue("price", price.price);
    form.setFieldValue("driverPrice", price.driverPrice);
    form.setFieldValue("priceId", price.id);
    form.setFieldValue("priority", price.priority);
    form.setFieldValue("additionalStopPrice", price.additionalStopPrice);
    form.setFieldValue(
      "additionalStopDriverPrice",
      price.additionalStopDriverPrice,
    );
    form.setFieldValue("waitingMinutePrice", price.waitingMinutePrice);
    form.setFieldValue(
      "waitingMinuteDriverPrice",
      price.waitingMinuteDriverPrice,
    );
    form.setFieldValue("waitingGraceMinutes", price.waitingGraceMinutes);
    form.setFieldValue("currencyId", price.currencyId ?? null);
    form.setFieldValue("companyIds", companyIds);
    setServicePrices(price.pricesByServices || []);
    setOpenModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePriceBetweenRegions(id);
      notification.success({
        message: `El precio se ha eliminado correctamente!`,
        placement: "bottom",
        duration: 2,
        onClose() {
          navigate(0);
        },
        key: "global",
      });
    } catch (error: any) {
      console.log(error);
      notification.error({
        message: error.response.data.message,
        placement: "bottom",
        key: "global",
      });
    }
  };

  const onSearch = async (value: string) => {
    setIgnoreFetch(false);
    setSearch(value);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  return (
    <ContainerLayout title={title} showNotification={false}>
      <Col span={24}>
        <Space>
          <Button
            onClick={() =>
              navigate(WebRoutes.CreatePriceRoute, {
                replace: true,
              })
            }
            type="primary"
            icon={<PlusCircleTwoTone />}
          >
            Precio
          </Button>
          <Text strong>Buscar</Text>
          <Search
            placeholder="Nombre de la region"
            allowClear
            onSearch={onSearch}
            loading={loading}
            disabled={loading}
          />
          <Text strong>Empresa</Text>
          <Select
            allowClear
            showSearch
            placeholder="Seleccione empresa"
            optionFilterProp="label"
            onChange={(val) => {
              setIgnoreFetch(false);
              setSelectedCompany(val);
              setTableParams({
                ...tableParams,
                pagination: { ...tableParams.pagination, current: 1 },
              });
            }}
            options={admins}
            style={{ width: 250 }}
            disabled={loading}
          />
        </Space>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          style={{ minWidth: "100%" }}
          columns={columns(handleClick, handleDelete, companyMap, currencyMap)}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ y: "calc(-250px + 97vh)" }}
        />
      </Col>

      {/* MODAL */}
      <Modal
        open={openModal}
        title="Modificar precios"
        footer={null}
        onCancel={() => setOpenModal(!openModal)}
        width={1000}
      >
        <Row justify="center" gutter={[24, 12]}>
          <Col span={8}>
            <Form
              form={form}
              name="basic"
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
            >
              <Form.Item label="Id" name="priceId">
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={1}
                  disabled
                />
              </Form.Item>

              <Form.Item label="Empresas" name="companyIds">
                <Select
                  mode="multiple"
                  placeholder="Seleccione una empresa"
                  showSearch
                  optionFilterProp="label"
                  options={admins}
                />
              </Form.Item>

              <Form.Item label="Precio" name="price">
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={1}
                />
              </Form.Item>

              <Form.Item label="Precio conductor" name="driverPrice">
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={1}
                />
              </Form.Item>

              <Form.Item
                label="Prioridad"
                name="priority"
                rules={[
                  {
                    required: true,
                    message: "Por favor ingresa una prioridad!",
                  },
                ]}
              >
                <InputNumber<number> style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item label="Moneda" name="currencyId">
                <Select
                  allowClear
                  placeholder="Seleccione una moneda"
                  showSearch
                  options={currencies}
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Form.Item label="Precio por paradas" name="additionalStopPrice">
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                />
              </Form.Item>

              <Form.Item
                label="Precio por paradas conductor"
                name="additionalStopDriverPrice"
              >
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                />
              </Form.Item>

              <Form.Item
                label="Precio por minuto de espera"
                name="waitingMinutePrice"
              >
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                />
              </Form.Item>

              <Form.Item
                label="Precio por minuto de espera conductor"
                name="waitingMinuteDriverPrice"
              >
                <InputNumber<number>
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                  min={0}
                />
              </Form.Item>

              <Form.Item
                label="Tolerancia de minutos de espera"
                name="waitingGraceMinutes"
              >
                <InputNumber<number> style={{ width: "100%" }} min={0} />
              </Form.Item>

              <Form.Item>
                <Space direction="horizontal">
                  <Button onClick={() => setOpenModal(!openModal)}>
                    Cancelar
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Guardar
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Col>

          <Col span={16}>
            <CreateItemsServices
              servicePrices={servicePrices}
              setServicePrices={setServicePrices}
              serviceTypes={serviceTypes}
            />
          </Col>
        </Row>
      </Modal>
    </ContainerLayout>
  );
};

export default PriceList;
